import type { ReactElement } from 'react';
import { Check } from 'lucide-react';
import {
  mcpRiskLevelColors,
  mcpRiskLevelLabels,
  type MCPRiskLevel,
  type MCPRiskUnavailableReason,
} from '@kirin/api-client/contracts/mcp';
import { MCP_RISK_UNAVAILABLE_COPY } from './mcpRiskCopy';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

/**
 * Pill tint + text per AgentMesh risk level (KIR-5108). The dot carries
 * AgentMesh's own tag colour verbatim (mcpRiskLevelColors); the tint and text
 * are a readable, theme-aware rendition of the same hue. The label is always
 * rendered, so colour is never the only signal.
 */
const LEVEL_CLASSES: Record<MCPRiskLevel, string> = {
  dangerous: 'bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-300',
  risky:
    'bg-yellow-50 text-yellow-800 dark:bg-yellow-950/40 dark:text-yellow-300',
  clean: 'bg-green-50 text-green-700 dark:bg-green-950/40 dark:text-green-300',
  partial_scan:
    'bg-orange-50 text-orange-700 dark:bg-orange-950/40 dark:text-orange-300',
  unscanned:
    'bg-neutral-100 text-neutral-600 dark:bg-neutral-800 dark:text-neutral-300',
};

interface McpRiskBadgeProps {
  level: MCPRiskLevel;
  size?: 'sm' | 'lg';
  className?: string;
}

/** AgentMesh risk value as a coloured pill: dot + label. */
export function McpRiskBadge({
  level,
  size = 'sm',
  className,
}: McpRiskBadgeProps): ReactElement {
  return (
    <span
      data-risk-level={level}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full font-semibold whitespace-nowrap',
        size === 'lg' ? 'px-3 py-1 text-sm' : 'px-2 py-0.5 text-xs',
        LEVEL_CLASSES[level],
        className
      )}
    >
      <span
        aria-hidden="true"
        data-testid="mcp-risk-dot"
        className={cn('rounded-full', size === 'lg' ? 'size-2' : 'size-1.5')}
        style={{ backgroundColor: mcpRiskLevelColors[level] }}
      />
      {mcpRiskLevelLabels[level]}
    </span>
  );
}

interface McpRiskUnavailableProps {
  reason: MCPRiskUnavailableReason | null | undefined;
}

/** The dash for a row with no AgentMesh value, explaining why on hover. */
export function McpRiskUnavailable({
  reason,
}: McpRiskUnavailableProps): ReactElement {
  const copy = MCP_RISK_UNAVAILABLE_COPY[reason ?? 'no-record'];
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          role="img"
          aria-label={copy}
          className="text-muted-foreground cursor-default"
          tabIndex={0}
        >
          —
        </span>
      </TooltipTrigger>
      <TooltipContent>
        <p>{copy}</p>
      </TooltipContent>
    </Tooltip>
  );
}

interface McpRiskFilterOptionProps {
  level: MCPRiskLevel;
  isSelected: boolean;
}

/** One option of the Risk column's multi-select filter. */
export function McpRiskFilterOption({
  level,
  isSelected,
}: McpRiskFilterOptionProps): ReactElement {
  return (
    <div className="flex items-center gap-3 flex-1 px-0.5 py-0.5">
      <div className="flex items-center justify-center w-4 h-4 shrink-0">
        {isSelected && (
          <Check className="size-4 text-primary" strokeWidth={2.5} />
        )}
      </div>
      <McpRiskBadge level={level} />
    </div>
  );
}
